import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { createAction, createReducer, createSelector, on, props, select, Store } from "@ngrx/store";
import { catchError, combineLatest, filter, map, mergeMap, of, take, tap, withLatestFrom } from "rxjs";
import { IEmployee, IEmployeeCreateRequest, IEmployeeRequest, IListEmployeesRequest } from "src/app/employee/employee";
import { AppStateInterface } from "../appState.interface";
import { EmployeeService } from "src/app/employee/services/employee.service";
import { NotificationService } from "@progress/kendo-angular-notification";
import { TranslocoService } from "@jsverse/transloco";
import { TimeSpan } from "src/app/shared/helpers/time-span";

export interface EmployeeStateInterface {
  isLoading: boolean,
  employees: IEmployee[],
  error: string | null
}

// actions
export const getEmployees = createAction('[Employees] Get Employees')
export const getEmployeesSuccess = createAction('[Employees] Get Employees success', props<{employees: IEmployee[]}>())
export const getEmployeesFailure = createAction('[Employees] Get Employees failure', props<{error: string}>())

export const listEmployees = createAction('[Employees] List Employees', props<{request: IListEmployeesRequest}>())
export const listEmployeesSuccess = createAction('[Employees] List Employees success', props<{employees: IEmployee[]}>())
export const listEmployeesFailure = createAction('[Employees] List Employees failure', props<{error: string}>())

// export const createEmployee = createAction('[Employees] Create Employees', props<{request: IEmployeeCreateRequest}>())
// export const createEmployeeSuccess = createAction('[Employees] Create Employees success', props<{employee: IEmployee}>())
// export const createEmployeeFailure = createAction('[Employees] Create Employees failure', props<{error: string}>())

// export const updateEmployee = createAction('[Employees] Update Employees', props<{request: IEmployeeRequest}>())
// export const updateEmployeeSuccess = createAction('[Employees] Update Employees success', props<{employee: IEmployee}>())
// export const updateEmployeeFailure = createAction('[Employees] Update Employees failure', props<{error: string}>())

export const deleteEmployee = createAction('[Employees] Delete Employees', props<{id: string}>())
export const deleteEmployeeSuccess = createAction('[Employees] Delete Employees success', props<{id: string}>())
export const deleteEmployeeFailure = createAction('[Employees] Delete Employees failure', props<{error: string}>())

// states
export const initialState: EmployeeStateInterface = {
  isLoading: false,
  employees: [],
  error: null
}

// reducers
export const employeesReducers = createReducer(
  initialState,
  on(getEmployees, (state) => ({...state, isLoading: true})),
  on(getEmployeesSuccess, (state,action) => ({
    ...state, 
    isLoading: false,
    employees: action.employees
  })),
  on(getEmployeesFailure, (state,action) => ({
    ...state, 
    isLoading: false,
    error: action.error
  })),

  // list
  on(listEmployees, (state) => ({...state, isLoading: true})),
  on(listEmployeesSuccess, (state,action) => ({
    ...state, 
    isLoading: false,
    employees: action.employees
  })),
  on(listEmployeesFailure, (state,action) => ({
    ...state, 
    isLoading: false,
    error: action.error
  })),

  // //create
  // on(createEmployee, (state) => ({...state, isLoading: true})),
  // on(createEmployeeSuccess, (state,action) => ({
  //   ...state, 
  //   isLoading: false,
  //   employees:[...state.employees, action.employee]
  // })),
  // on(createEmployeeFailure, (state,action) => ({
  //   ...state, 
  //   isLoading: false,
  //   error: action.error
  // })),

  // // update
  // on(updateEmployee, (state) => ({...state, isLoading: true})),
  // on(updateEmployeeSuccess, (state,action) => ({
  //   ...state, 
  //   isLoading: false,
  //   employees: state.employees.map(employee => employee.id === action.employee.id ? action.employee : employee)
  // })),
  // on(updateEmployeeFailure, (state,action) => ({
  //   ...state, 
  //   isLoading: false,
  //   error: action.error
  // })),

  //delete
  on(deleteEmployee, (state) => ({...state, isLoading: true})),
  on(deleteEmployeeSuccess, (state,action) => ({
    ...state, 
    isLoading: false,
    employees: state.employees.filter(employee =>
      employee.id !== action.id),
  })),
  on(deleteEmployeeFailure, (state,action) => ({
    ...state, 
    isLoading: false,
    error: action.error
  }))
)

export const selectFeature = (state: AppStateInterface) => state.employees

// Selectors
export const isLoadingSelector = createSelector(selectFeature, (state) => state.isLoading); 
export const errorSelector = createSelector(selectFeature, (state) => state.error); 
export const employeesSelector = createSelector(selectFeature, (state) => state.employees);
export const activeEmployeesSelector = createSelector(selectFeature, (state) => state.employees.filter(employee => employee.isHidden === false));

// effects


@Injectable()
export class EmployeesEffects {
  getEmployees$ = createEffect(() => {
      return this.actions$.pipe(
          ofType(getEmployees),
          mergeMap(() =>{
            return this.employeeService
              .listEmployees({ pageNumber: 1, pageSize: 999999, showHidden: true })
              .pipe(
                map((response) => getEmployeesSuccess({
                  employees: this.createTimeObjects(response.data)
                })),
                catchError((error) => of(getEmployeesFailure({error: error})))
              );
            }),
          take(1) // makes sure this the api call gets executed once
          // JE 2024-08-22: deactived since the admin does not get the newest data when editing an employee since that data is an an component store
        )
    });

    // separate action for base data admin which can be refreshed
  listEmployees$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(listEmployees),
        mergeMap(({request}) =>{
          return this.employeeService
            .listEmployees(request)
            .pipe(
              map((response) => listEmployeesSuccess({
                employees: this.createTimeObjects(response.data)
              })),
              catchError((error) => of(listEmployeesFailure({error: error})))
            );
          }),
      )
  });

  // createEmployee$ = createEffect(() => {
  //   return this.actions$.pipe(
  //       ofType(createEmployee),
  //       mergeMap(({request}) =>{
  //         return this.employeeService.createEmployee(request)
  //           .pipe(
  //             filter(x => x ? true : false),
  //             map((response) => createEmployeeSuccess({
  //               employee: response.employee
  //             })),
  //             tap(() => this.successNotification()),
  //             catchError((error) => of(createEmployeeFailure({error: error})))
  //           );
  //         }),
  //     )
  // });

  // updateEmployee$ = createEffect(() => {
  //   return this.actions$.pipe(
  //       ofType(updateEmployee),
  //       mergeMap(({request}) =>{
  //         return this.employeeService
  //           .updateEmployee(request)
  //           .pipe(
  //             filter(x => x ? true : false),
  //             map((employee) => updateEmployeeSuccess({
  //               employee: employee
  //             })),
  //             tap(() => this.successNotification()),
  //             catchError((error) => of(createEmployeeFailure({error: error})))
  //           );
  //         }),
  //     )
  // });

  deleteEmployee$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(deleteEmployee),
        mergeMap(({id}) =>{
          return this.employeeService
            .deleteEmployee(id)
            .pipe(
              filter(x => x ? true : false),
              map((response) => deleteEmployeeSuccess({
                id: id
              })),
              tap(() => this.successNotification('deleteSuccess')),
              catchError((error) => of(deleteEmployeeFailure({error: error})))
            );
          }),
      )
  });

  successNotification(message: string = null){
    this.notification.show({
      content: this.translocoService.translate(message ?? 'saveSuccess'),
      animation: { type: 'fade', duration: 500 },
      position: { horizontal: 'center', vertical: 'top' },
      type: { style: 'success', icon: true },
      hideAfter: 3000
    });
  }

  createTimeObjects(employees : IEmployee[]){
    return employees.map((employee) =>{
      const name = `${employee.firstName} ${employee.lastName}`;
      return {
        ...employee,
        fullname: name,
        flexTimeBalanceObj: TimeSpan.parse(employee.flexTimeBalance).getAsTimeObject(),
        preWorkTimeBalanceObj: TimeSpan.parse(employee.preWorkTimeBalance).getAsTimeObject()
      }
    })
  }

  constructor(
    private actions$: Actions,
    private employeeService: EmployeeService,
    private notification: NotificationService,
    private translocoService: TranslocoService
  ) {

  }
}